import "./clientWebsites.css";
import { api, ApiError } from "../api";
import { errorBanner, loadingBlock } from "./shared/blocks";
import { createLifecycle, isAbort } from "./shared/lifecycle";
import { h, tidy } from "./shared/safe";
import { clientWorkspaceNav } from "./client/workspaceNav";

type Client = { name: string; slug: string; category: string | null; city: string | null; state: string | null; moneySite: string | null };
type Website = {
  websiteId: string; type: "kit" | "legacy"; siteId: string | null; siteKind: string; template: string | null;
  destinationUrl: string | null; platform: string | null; state: string; removedAt: string | null;
  currentReleaseNumber?: number; latestRevision?: number; lastUpdatedAt: string | null;
  publications: { platform: string; destinationUrl: string; state: string; lastUpdatedAt: string | null }[];
};
type Kit = { kitId: string; title: string; family: string };
type Connection = { platform: string; status: string };
type GroupBy = "platform" | "type" | "status" | "none";
const enc = encodeURIComponent;
const PREFS = "cloudpush.client-websites.preferences.v1";

function savedPrefs(): { groupBy: GroupBy; mode: "grid" | "table"; collapsed: string[]; query: string; type: string; status: string; platform: string } {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS) ?? "{}");
    return {
      groupBy: ["platform", "type", "status", "none"].includes(raw.groupBy) ? raw.groupBy : "platform",
      mode: raw.mode === "table" ? "table" : "grid",
      collapsed: Array.isArray(raw.collapsed) ? raw.collapsed.filter((x: unknown) => typeof x === "string") : [],
      query: typeof raw.query === "string" ? raw.query.slice(0, 200) : "",
      type: typeof raw.type === "string" ? raw.type.slice(0, 100) : "",
      status: typeof raw.status === "string" ? raw.status.slice(0, 100) : "",
      platform: typeof raw.platform === "string" ? raw.platform.slice(0, 100) : "",
    };
  } catch { return { groupBy: "platform", mode: "grid", collapsed: [], query: "", type: "", status: "", platform: "" }; }
}

function canonicalStatus(site: Website): string {
  if (site.removedAt || site.state === "removed") return "Removed";
  const states = site.publications.map((p) => p.state.toLowerCase());
  if (states.some((s) => ["queued", "pending", "publishing", "running", "needs_reconciliation", "unknown"].includes(s))) return "Pending publication";
  const live = states.some((s) => ["live", "published", "delivered", "observed", "active"].includes(s));
  const changed = !!site.latestRevision && !!site.currentReleaseNumber && site.state === "draft";
  if (live && changed) return "Live with draft changes";
  if (live) return "Published";
  if (site.state === "draft") return "Draft";
  if (site.state === "released") return "Release saved · publication not confirmed";
  const val = tidy(site.state.replaceAll("_", " "), 60);
  if (val.toLowerCase() === "live" || val.toLowerCase() === "published") return "Published";
  if (val.toLowerCase() === "awaiting approval" || val.toLowerCase() === "pending approval") return "Awaiting approval";
  return val ? val[0]!.toUpperCase() + val.slice(1) : "Status unavailable";
}

function updated(value: string | null): string {
  if (!value) return "Update time unavailable";
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? "Update time unavailable" : `Updated ${new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date)}`;
}

function hostOf(raw: string | null): string {
  if (!raw) return "Website address unavailable";
  try { return new URL(raw).hostname; } catch { return tidy(raw, 120); }
}

export async function clientWebsitesView(root: HTMLElement, params: Record<string, string>): Promise<void> {
  const clientId = typeof params.id === "string" ? params.id : "";
  const page = h("section", { class: "qf cp-client-websites" });
  root.replaceChildren(page);
  if (!clientId) return void page.append(errorBanner("This page needs a client. Go back to Clients and open a workspace."));
  const life = createLifecycle(page);
  const pref = savedPrefs();
  let client: Client | null = null;
  let websites: Website[] | null = null;
  let kits: Kit[] = [];
  let connections: Connection[] = [];

  page.append(loadingBlock("Loading this client’s websites", 5));
  try {
    const [clientRaw, websiteRaw, kitRaw, connectionRaw] = await Promise.all([
      api<unknown>(`/v1/clients/${enc(clientId)}`),
      api<unknown>(`/v1/clients/${enc(clientId)}/websites`),
      api<unknown>("/v1/site-kits?view=summary"),
      api<unknown>(`/v1/connections?clientId=${enc(clientId)}`).catch(() => null),
    ]);
    if (!life.alive()) return;
    const c = clientRaw && typeof clientRaw === "object" ? (clientRaw as Record<string, unknown>).client : null;
    if (!c || typeof c !== "object") throw new Error("The client record could not be read.");
    const cr = c as Record<string, unknown>;
    client = {
      name: tidy(typeof cr.business_name === "string" ? cr.business_name : "Client", 180),
      slug: tidy(typeof cr.slug === "string" ? cr.slug : "", 120),
      category: typeof cr.primary_category === "string" ? tidy(cr.primary_category, 100) : null,
      city: typeof cr.city === "string" ? tidy(cr.city, 80) : null,
      state: typeof cr.state === "string" ? tidy(cr.state, 40) : null,
      moneySite: typeof cr.money_site_url === "string" ? cr.money_site_url : null,
    };
    const w = websiteRaw && typeof websiteRaw === "object" ? (websiteRaw as Record<string, unknown>).websites : null;
    if (!Array.isArray(w)) throw new Error("The website list did not match the format this page supports.");
    websites = w.filter((x): x is Record<string, unknown> => !!x && typeof x === "object").map((x) => ({
      websiteId: typeof x.websiteId === "string" ? x.websiteId : "",
      type: x.type === "kit" ? ("kit" as const) : ("legacy" as const),
      siteId: typeof x.siteId === "string" ? x.siteId : null,
      siteKind: typeof x.siteKind === "string" ? x.siteKind : "unknown",
      template: typeof x.template === "string" ? x.template : null,
      destinationUrl: typeof x.destinationUrl === "string" ? x.destinationUrl : null,
      platform: typeof x.platform === "string" ? x.platform : null,
      state: typeof x.state === "string" ? x.state : "unknown",
      removedAt: typeof x.removedAt === "string" ? x.removedAt : null,
      currentReleaseNumber: typeof x.currentReleaseNumber === "number" ? x.currentReleaseNumber : undefined,
      latestRevision: typeof x.latestRevision === "number" ? x.latestRevision : undefined,
      lastUpdatedAt: typeof x.lastUpdatedAt === "string" ? x.lastUpdatedAt : null,
      publications: Array.isArray(x.publications) ? x.publications.filter((p): p is Record<string, unknown> => !!p && typeof p === "object").map((p) => ({
        platform: typeof p.platform === "string" ? p.platform : "",
        destinationUrl: typeof p.destinationUrl === "string" ? p.destinationUrl : "",
        state: typeof p.state === "string" ? p.state : "unknown",
        lastUpdatedAt: typeof p.lastUpdatedAt === "string" ? p.lastUpdatedAt : null,
      })) : [],
    })).filter((x) => x.websiteId);
    const k = kitRaw && typeof kitRaw === "object" ? (kitRaw as Record<string, unknown>).kits : null;
    kits = Array.isArray(k) ? k.filter((x): x is Record<string, unknown> => !!x && typeof x === "object" && typeof (x as Record<string, unknown>).kitId === "string").map((x) => ({ kitId: x.kitId as string, title: typeof x.title === "string" ? tidy(x.title, 100) : x.kitId as string, family: typeof x.family === "string" ? tidy(x.family, 60) : "" })) : [];
    const rawConnections = connectionRaw && typeof connectionRaw === "object" ? (connectionRaw as Record<string, unknown>).connections : null;
    connections = Array.isArray(rawConnections) ? rawConnections.filter((x): x is Record<string, unknown> => !!x && typeof x === "object" && typeof (x as Record<string, unknown>).platform === "string").map((x) => ({ platform: tidy(x.platform as string, 80), status: typeof x.status === "string" ? tidy(x.status, 40) : "unknown" })) : [];
    draw();
  } catch (e) {
    if (!life.alive() || isAbort(e)) return;
    const message = e instanceof ApiError ? e.status === 404 ? "This client no longer exists or is unavailable." : `Websites could not be loaded (HTTP ${e.status}).` : e instanceof Error ? e.message : "Websites could not be loaded.";
    page.replaceChildren(errorBanner(message, { retry: () => void clientWebsitesView(root, params) }));
  }

  function draw() {
    if (!client || !websites) return;
    const cid = enc(clientId);
    const title = h("div", { class: "cp-cw-heading" },
      h("div", null,
        h("a", { class: "cp-cw-back", href: "#/clients" }, "← Clients"),
        h("p", { class: "cp-cw-eyebrow" }, "Client workspace · Websites"),
        h("h1", null, client.name),
        h("p", { class: "cp-cw-subtitle" }, [client.category, [client.city, client.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ") || client.slug),
      ),
      h("div", { class: "cp-cw-actions" },
        h("a", { class: "cp-btn cp-btn--secondary", href: `#/clients/${cid}/facts` }, "Business details"),
        h("a", { class: "cp-btn", href: `#/clients/${cid}/site` }, "＋ Create website"),
      ),
    );
    const tabs = clientWorkspaceNav(clientId, "Websites", client.name);
    const liveCount = websites.filter((s) => canonicalStatus(s) === "Published" || canonicalStatus(s) === "Live with draft changes").length;
    const draftCount = websites.filter((s) => canonicalStatus(s) === "Draft").length;
    const reviewCount = websites.filter((s) => canonicalStatus(s) === "Pending publication").length;
    const stats = h("div", { class: "cp-cw-stats", "aria-label": "Website summary" },
      ...[[websites.length, "Websites"], [liveCount, "Published"], [draftCount, "Drafts"], [reviewCount, "Pending"]].map(([n, label]) => h("div", { class: "cp-cw-stat" }, h("strong", null, String(n)), h("span", null, String(label)))),
    );
    const search = h("input", { type: "search", value: pref.query, placeholder: "Search websites or provider URLs", "aria-label": "Search websites", autocomplete: "off" }) as HTMLInputElement;
    const groupSelect = h("select", { "aria-label": "Group websites by", on: { change: (e: Event) => { pref.groupBy = (e.target as HTMLSelectElement).value as GroupBy; persist(); paint(); } } },
      ...[["platform", "Platform"], ["type", "Website type"], ["status", "Status"], ["none", "None"]].map(([v, label]) => h("option", { value: v, selected: pref.groupBy === v }, label)),
    );
    const typeSelect = h("select", { "aria-label": "Filter by website type" }, h("option", { value: "" }, "All website types"));
    const statusSelect = h("select", { "aria-label": "Filter by status" }, h("option", { value: "" }, "All statuses"));
    const platformSelect = h("select", { "aria-label": "Filter by platform" }, h("option", { value: "" }, "All platforms"));
    const modeToggle = h("div", { class: "cp-cw-view", role: "group", "aria-label": "Website display" },
      h("button", { type: "button", class: pref.mode === "grid" ? "is-active" : "", "aria-pressed": String(pref.mode === "grid"), "aria-label": "Grid view", on: { click: () => { pref.mode = "grid"; persist(); paint(); } } }, "▦"),
      h("button", { type: "button", class: pref.mode === "table" ? "is-active" : "", "aria-pressed": String(pref.mode === "table"), "aria-label": "Table view", on: { click: () => { pref.mode = "table"; persist(); paint(); } } }, "☷"),
    );
    const count = h("p", { class: "cp-cw-count", role: "status", "aria-live": "polite" });
    const results = h("div", { class: "cp-cw-groups" });
    const toolbar = h("div", { class: "cp-cw-toolbar" }, search, groupSelect, typeSelect, statusSelect, platformSelect, modeToggle);
    const essentials = h("section", { class: "cp-cw-essential" },
      h("h2", null, "Business essentials"),
      h("p", null, h("span", null, "Official website"), client.moneySite ? h("a", { href: client.moneySite, target: "_blank", rel: "noopener noreferrer" }, hostOf(client.moneySite), " ↗") : h("span", { class: "cp-cw-muted" }, "Not recorded")),
      h("a", { class: "cp-cw-link", href: `#/clients/${cid}/facts` }, "View business details →"),
    );
    const platformIssues = [...new Set(websites.filter((s) => s.platform && !["removed", "deleted"].includes(canonicalStatus(s).toLowerCase())).map((s) => s.platform!))].filter((platform) => !connections.some((c) => c.platform === platform && ["connected", "expiring"].includes(c.status)));
    const issueNotice = platformIssues.length ? h("div", { class: "cp-cw-connection-issue", role: "status" },
      h("strong", null, `${platformIssues.length} website platform${platformIssues.length === 1 ? " needs" : "s need"} a connection check`),
      h("p", null, `${platformIssues.slice(0, 3).join(", ")}${platformIssues.length > 3 ? ` and ${platformIssues.length - 3} more` : ""} may be disconnected or unavailable. Saved websites remain listed.`),
      h("a", { href: "#/connections" }, "Review connections →"),
    ) : null;
    page.replaceChildren(title, tabs, stats, h("div", { class: "cp-cw-layout" }, h("div", { class: "cp-cw-library" },
      h("div", { class: "cp-cw-library-title" }, h("div", null, h("h2", null, "Website library"), h("p", null, "Each saved website stays an independent record.")), h("span", { class: "cp-cw-total" }, `${websites.length} total`)),
      toolbar, issueNotice, count, results,
    ), h("aside", { class: "cp-cw-aside" }, essentials)),
    );
    const filterSelects = [typeSelect, statusSelect, platformSelect];
    const values = websites;
    const options = [
      [...new Set(values.map((s) => typeName(s)).filter(Boolean))].sort(),
      [...new Set(values.map(canonicalStatus))].sort(),
      [...new Set(values.map((s) => s.platform || "Platform not recorded"))].sort(),
    ];
    filterSelects.forEach((sel, i) => {
      const prior = sel.value;
      const first = sel.options[0]!;
      const labels = ["All website types", "All statuses", "All platforms"];
      sel.replaceChildren(first, ...options[i]!.map((v) => h("option", { value: v }, v)));
      first.textContent = labels[i]!;
      if (options[i]!.includes(prior)) sel.value = prior;
      sel.addEventListener("change", () => { pref[["type", "status", "platform"][i] as "type" | "status" | "platform"] = sel.value; persist(); paint(); });
    });
    typeSelect.value = options[0]!.includes(pref.type) ? pref.type : "";
    statusSelect.value = options[1]!.includes(pref.status) ? pref.status : "";
    platformSelect.value = options[2]!.includes(pref.platform) ? pref.platform : "";
    search.addEventListener("input", () => { pref.query = search.value; persist(); paint(); });
    paint();

    function typeName(site: Website): string {
      if (site.type !== "kit" || !site.template) return "Unknown website type";
      const kit = kits.find((k) => k.kitId === site.template);
      const labels: Record<string, string> = { profile: "Business profile", blog: "Blog", directory: "Directory", wiki: "Wiki", service_hub: "Service and location guide", resource: "Magazine / resource hub", case_study: "Case studies" };
      return kit ? labels[kit.family] ?? "Unknown website type" : "Unknown website type";
    }
    function persist() { try { localStorage.setItem(PREFS, JSON.stringify(pref)); } catch { /* keep this visit usable */ } }
    function paint() {
      const q = search.value.trim().toLowerCase();
      const filtered = values.filter((s) => {
        const type = typeName(s), status = canonicalStatus(s), platform = s.platform || "Platform not recorded";
        return (!q || `${hostOf(s.destinationUrl)} ${s.destinationUrl ?? ""} ${s.template ?? ""} ${type} ${platform}`.toLowerCase().includes(q)) && (!typeSelect.value || typeSelect.value === type) && (!statusSelect.value || statusSelect.value === status) && (!platformSelect.value || platformSelect.value === platform);
      });
      count.textContent = `${filtered.length} of ${values.length} websites${q || typeSelect.value || statusSelect.value || platformSelect.value ? " match these filters" : ""}.`;
      if (!filtered.length) return void results.replaceChildren(h("div", { class: "cp-cw-empty" }, h("strong", null, values.length ? "No websites match those filters" : "No saved websites yet"), h("p", null, values.length ? "Clear a filter or try another search." : "Choose a complete website template to start this client’s first site."), values.length ? null : h("a", { class: "cp-btn", href: `#/clients/${cid}/site` }, "Create website")));
      const groupKey = (s: Website) => pref.groupBy === "platform" ? s.platform || "Platform not recorded" : pref.groupBy === "type" ? typeName(s) : pref.groupBy === "status" ? canonicalStatus(s) : "All websites";
      const grouped = new Map<string, Website[]>();
      for (const s of filtered) { const key = groupKey(s); grouped.set(key, [...(grouped.get(key) ?? []), s]); }
      results.classList.toggle("is-table", pref.mode === "table");
      results.replaceChildren(...[...grouped.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([name, rows]) => renderGroup(name, rows)));
    }
    function renderGroup(name: string, rows: Website[]) {
      const collapsed = pref.collapsed.includes(name);
      const counts = rows.reduce((acc, s) => { const status = canonicalStatus(s); acc[status] = (acc[status] ?? 0) + 1; return acc; }, {} as Record<string, number>);
      const summary = Object.entries(counts).map(([k, v]) => `${v} ${k.toLowerCase()}`).join(" · ");
      const platform = pref.groupBy === "platform" && name !== "Platform not recorded" ? name : "";
      const head = h("div", { class: "cp-cw-group-head" },
        h("button", { type: "button", class: "cp-cw-collapse", "aria-expanded": String(!collapsed), "aria-label": `${collapsed ? "Expand" : "Collapse"} ${name}`, on: { click: () => { pref.collapsed = collapsed ? pref.collapsed.filter((x) => x !== name) : [...pref.collapsed, name]; persist(); paint(); } } }, collapsed ? "›" : "⌄"),
        h("div", { class: "cp-cw-group-name" }, h("h3", null, name), h("p", null, `${rows.length} ${rows.length === 1 ? "website" : "websites"} · ${summary}`)),
        platform ? h("a", { class: "cp-btn cp-btn--secondary cp-cw-group-create", href: `#/clients/${cid}/site?platform=${enc(platform)}`, "aria-label": `Create a website on ${platform}` }, "＋ Website") : null,
      );
      const cards = pref.mode === "grid" ? h("div", { class: "cp-cw-cards" }, ...rows.map(card)) : table(rows);
      return h("section", { class: `cp-cw-group${collapsed ? " is-collapsed" : ""}` }, head, collapsed ? null : cards);
    }
    function card(s: Website) {
      const status = canonicalStatus(s);
      const type = typeName(s);
      const href = s.type === "kit" && s.siteId ? `#/clients/${cid}/site?siteId=${enc(s.siteId)}` : `#/clients/${cid}/site?siteId=${enc(s.websiteId)}`;
      const provider = s.platform || "Platform not recorded";
      const actions = s.type === "kit" ? (status === "Pending publication" ? "Review website" : status === "Draft" || status === "Release saved · publication not confirmed" ? "Continue editing" : "Manage website") : "View website details";
      return h("article", { class: "cp-cw-card" },
        h("div", { class: "cp-cw-preview", "aria-label": "Website preview unavailable" }, h("span", null, "Preview unavailable")),
        h("div", { class: "cp-cw-card-body" },
          h("div", { class: "cp-cw-card-top" }, h("h4", null, hostOf(s.destinationUrl)), h("span", { class: `cp-cw-badge cp-cw-badge--${statusTone(status)}` }, status)),
          h("div", { class: "cp-cw-meta" }, h("span", { class: "cp-cw-type" }, type), h("span", null, provider)),
          h("p", { class: "cp-cw-address" }, s.destinationUrl ? h("a", { href: s.destinationUrl, target: "_blank", rel: "noopener noreferrer", title: s.destinationUrl }, s.destinationUrl, " ↗") : "Address unavailable"),
          h("p", { class: "cp-cw-updated" }, updated(s.lastUpdatedAt)),
          s.type === "legacy" ? h("p", { class: "cp-cw-legacy-note" }, "Legacy website · CloudPush does not have an editable saved source for this record.") : null,
          h("a", { class: "cp-btn cp-cw-open", href }, actions),
        ),
      );
    }
    function table(rows: Website[]) {
      return h("div", { class: "cp-cw-table-scroll", role: "region", "aria-label": `${nameForTable()} website list`, tabindex: "0" }, h("table", { class: "cp-cw-table" },
        h("thead", null, h("tr", null, ...["Website", "Type", "Platform", "Status", "Updated", "Action"].map((x) => h("th", { scope: "col" }, x)))),
        h("tbody", null, ...rows.map((s) => {
          const status = canonicalStatus(s), cid = enc(clientId);
          const href = s.type === "kit" && s.siteId ? `#/clients/${cid}/site?siteId=${enc(s.siteId)}` : `#/clients/${cid}/site?siteId=${enc(s.websiteId)}`;
          return h("tr", null,
            h("td", null, h("strong", null, hostOf(s.destinationUrl)), h("div", { class: "cp-cw-address" }, s.destinationUrl ?? "Address unavailable")),
            h("td", null, typeName(s)), h("td", null, s.platform || "Platform not recorded"),
            h("td", null, h("span", { class: `cp-cw-badge cp-cw-badge--${statusTone(status)}` }, status)),
            h("td", null, updated(s.lastUpdatedAt)), h("td", null, h("a", { href, class: "cp-cw-link" }, s.type === "legacy" ? "View details" : "Manage")),
          );
        })),
      ));
    }
    function nameForTable() { return client?.name ?? "Client"; }
  }
}

function statusTone(status: string): string {
  if (["Published", "Live with draft changes"].includes(status)) return "success";
  if (status === "Removed") return "muted";
  if (["Draft", "Pending publication", "Release saved · publication not confirmed"].includes(status)) return "warning";
  return "neutral";
}
